import { Theme } from "@material-ui/core";

import { IRollups } from "modules/projects/types/entities";

export enum RollupsItemType {
  All = "all",
  Collection = "collection",
  Group = "group",
  Project = "project"
}

export interface IRollupsItem {
  label: string;
  value: string;
  type: RollupsItemType;
  level: 0 | 1 | 2;
  disabled?: boolean;
}

export const getOptionsFromData = ({
  data,
  includeAll = true,
  skipUngrouped = false
}: {
  data?: IRollups;
  includeAll?: boolean;
  skipUngrouped?: boolean;
}): IRollupsItem[] => {
  if (data) {
    let options: IRollupsItem[] = [];

    if (includeAll) {
      options.push({
        label: "All projects",
        value: "all",
        type: RollupsItemType.All,
        level: 0
      });
    }

    options = options.concat(getOptions({ data, skipUngrouped }));

    return options;
  }

  return [];
};

export const getOptions = ({
  data,
  skipUngrouped
}: {
  data: IRollups;
  skipUngrouped?: boolean;
}): IRollupsItem[] => {
  const includedGroups = data.projects
    .map(project => project.group_id)
    .filter(Boolean);

  const includedCollections = data.groups
    .filter(group => group.collection_id && includedGroups.includes(group.id))
    .map(group => group.collection_id);

  const options: IRollupsItem[] = [];

  const addOption = (
    optionData: { id: string; name: string },
    type: RollupsItemType,
    level: 0 | 1 | 2,
    disabled?: boolean
  ) => {
    options.push({
      label: optionData.name,
      value: optionData.id,
      type,
      level,
      disabled
    });
  };

  const getSortedProjects = (projects: IRollups["projects"]) => {
    return projects.sort((a, b) => a.name.localeCompare(b.name));
  };

  // collections
  data.collections.forEach(collection => {
    if (includedCollections.includes(collection.id)) {
      addOption(collection, RollupsItemType.Collection, 0);

      data.groups.forEach(group => {
        if (
          group.collection_id === collection.id &&
          includedGroups.includes(group.id)
        ) {
          addOption(group, RollupsItemType.Group, 1);

          getSortedProjects(data.projects).forEach(project => {
            if (project.group_id === group.id) {
              addOption(project, RollupsItemType.Project, 2);
            }
          });
        }
      });
    }
  });

  // groups
  data.groups.forEach(group => {
    if (!group.collection_id && includedGroups.includes(group.id)) {
      addOption(group, RollupsItemType.Group, 0);

      getSortedProjects(data.projects).forEach(project => {
        if (project.group_id === group.id) {
          addOption(project, RollupsItemType.Project, 1);
        }
      });
    }
  });

  // projects
  const hasUngroupedProjects = data.projects.some(project => !project.group_id);

  if (hasUngroupedProjects && !skipUngrouped) {
    addOption(
      {
        id: `ungrouped`,
        name: "Ungrouped Projects"
      },
      RollupsItemType.Project,
      0,
      true
    );
  }

  getSortedProjects(data.projects).forEach(project => {
    if (!project.group_id) {
      addOption(project, RollupsItemType.Project, 0);
    }
  });

  return options;
};

export const searchOptions = ({
  options,
  data,
  term
}: {
  options: IRollupsItem[];
  data: IRollups;
  term: string;
}): IRollupsItem[] => {
  const matchedOptions = options.filter(option => {
    if (option.type === RollupsItemType.All) {
      return true;
    }

    if (option.type === RollupsItemType.Collection) {
      const isMatching = option.label.toLowerCase().includes(term);
      const relatedGroups = data.groups
        .filter(group => group.collection_id === option.value)
        .map(group => group.id);

      const hasMatchingGroups = data.groups.some(
        group =>
          group.collection_id === option.value &&
          group.name.toLowerCase().includes(term)
      );
      const hasMatchingProjects = data.projects.some(
        project =>
          project.group_id &&
          relatedGroups.includes(project.group_id) &&
          project.name.toLowerCase().includes(term)
      );

      return isMatching || hasMatchingGroups || hasMatchingProjects;
    }

    if (option.type === RollupsItemType.Group) {
      const isMatching = option.label.toLowerCase().includes(term);

      const hasMatchingProjects = data.projects.some(
        project =>
          project.group_id === option.value &&
          project.name.toLowerCase().includes(term)
      );

      return isMatching || hasMatchingProjects;
    }

    return term
      ? option.label !== "Ungrouped Projects" &&
          option.label.toLowerCase().includes(term)
      : option.label.toLowerCase().includes(term);
  });

  return matchedOptions;
};

export const getTypeIconColor = (type: RollupsItemType, theme: Theme) =>
  ({
    collection: theme.palette.primary.main,
    group: theme.palette.secondary.main,
    project: theme.palette.text.primary,
    all: theme.palette.text.primary
  }[type]);
